import { keepPreviousData, QueryKey, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';
import {
  AgentDetailsUser,
  Competition,
  Customer,
  ComplyCubeFlowSession,
  CustomerSettings,
  EventType,
  GlobalTicketConditionsResponse,
  GroupedPublishedEventsResponse,
  JackpotPool,
  Market,
  MarketGroup,
  PaginatedData,
  PublishedEventListWithTopMarketType,
  ResponseData,
  Scoreboard,
  SlipBonusCampaigns,
  Sport,
  SportsbookReport,
  UserBalance,
  BetslipWithBetsExtendedInfo,
} from '../@types';
import { CURRENCY, QUERY_KEYS, SECOND, API_ROUTES } from '../constants';
import { getData, patchData, postData } from '../utils/api';
import { useMarketplaceConfig } from './marketplace';
import { getBalanceData } from '../helpers';
import { useInvalidateQuery } from '../hooks/useInvalidateQuery';
import useKycAttempts from '../hooks/useKycAttempts';
import { getDisplayName } from '../utils';
import { useRoles } from '../hooks/useRoles';
import { USER_ROLES, UserRole } from '../constants/roles';
import { QueryParams, usePagination } from '../hooks/usePagination';
import { WalletTransaction, WalletTransactionTotals } from '../@types/wallet';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { enqueueSnackbar } from 'notistack';
import useLocalization from '../hooks/useLocalization';
import useInfiniteScroll, { InfiniteScrollOptions } from '../hooks/useInfiniteScroll';
import { CasinoTransaction } from '../types/transactions';
import { hasCasinoOnlyProducts, hasSportsbookOnlyProducts } from '../helpers/products';
import { PRODUCT } from '../types/enums';
export { useJackpotPoolStatistics } from './jackpots';

export const useBalances = (options?: Pick<UseQueryOptions<ResponseData<UserBalance>>, 'refetchInterval'>) => {
  const { data: marketplaceData } = useMarketplaceConfig();
  const iso3 = marketplaceData?.marketplaceConfig.currency ?? CURRENCY.code;
  const { data, isPending, refetch } = useQuery({
    queryKey: [QUERY_KEYS.balance],
    queryFn: (): Promise<ResponseData<UserBalance>> => getData('wallets/balances', { currency: iso3 }, 'wallet'),
    ...options,
  });
  const mainBalance = getBalanceData(data?.items, 'main');
  const bonusBalance = getBalanceData(data?.items, 'bonus');
  return {
    mainBalance,
    bonusBalance,
    isLoading: isPending,
    refetch,
  };
};

export const useCountPendingBetslips = (options?: Omit<UseQueryOptions<{ count: number }>, 'queryKey' | 'queryFn'>) => {
  const { isPlayer } = useRoles();

  return useQuery({
    queryKey: [QUERY_KEYS.pendingBetslipsCount],
    queryFn: (): Promise<{ count: number }> =>
      getData(isPlayer ? '/betting-slips/my-bets/count-pending' : '/bo/betting-slips/my-bets/count-pending'),
    ...options,
  });
};

export const useGetEventScoreboard = (
  eventId: string,
  options?: Omit<UseQueryOptions<Scoreboard>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: [QUERY_KEYS.eventPartScores, eventId],
    queryFn: (): Promise<Scoreboard> => getData(`events/${eventId}/scoreboard`),
    enabled: !!eventId,
    ...options,
    gcTime: 0,
  });
};

type UseSportsQueryOptions = Pick<UseQueryOptions<ResponseData<Sport>>, 'enabled'> & {
  queryParams: {
    isLive: boolean;
    includeEventCount?: boolean;
    [key: string]: unknown;
  };
};

export const useSports = (options: UseSportsQueryOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.sports, options.queryParams],
    queryFn: (): Promise<ResponseData<Sport>> => getData('sports/with-events', options.queryParams),
    select: (data) => data.items.map((sport) => ({ ...sport, name: getDisplayName(sport) })),
    staleTime: options.queryParams.isLive === false ? Infinity : undefined,
    ...options,
  });
};

export const useSportById = (sportId: string | undefined) => {
  return useQuery({
    queryKey: [QUERY_KEYS.sports, sportId],
    queryFn: (): Promise<{ item: Sport }> => getData(`sports/${sportId}`),
    enabled: !!sportId,
    select: (data) => ({
      ...data.item,
      displayName: getDisplayName(data.item),
    }),
    staleTime: Infinity,
  });
};

export const useFavoriteEventsCount = () => {
  const { isPlayer } = useRoles();

  const query = useQuery({
    queryKey: [QUERY_KEYS.favoriteEvents, 'count'],
    queryFn: (): Promise<{ eventsCount: number; tournamentsCount: number }> =>
      getData('user-settings/events/favourites/count'),
    enabled: isPlayer,
  });

  return {
    ...query,
    isEnabled: isPlayer,
  };
};

export const useMarketGroups = (sportId: string | undefined, queryParams: { [key: string]: unknown } = {}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.marketGroups, sportId, queryParams],
    queryFn: (): Promise<{ items: MarketGroup[] }> =>
      getData('market-type-groups', {
        sportId,
        displayType: 'match',
        sort: 'position',
        isActive: true,
        limit: 100,
        ...queryParams,
      }),
    enabled: !!sportId,
    select: (data) => data.items,
    staleTime: 5 * 60 * 1000,
  });
};

type UseMarketsCountOptions = {
  eventId: string | undefined;
  marketTypeGroupId: string | undefined;
  isLive: boolean;
  enabled?: boolean;
};

export const useMarketsCount = ({ eventId, marketTypeGroupId, isLive, enabled = true }: UseMarketsCountOptions) => {
  return useQuery({
    queryKey: [QUERY_KEYS.markets, 'count', eventId, marketTypeGroupId, isLive],
    queryFn: (): Promise<number> =>
      getData('markets/published/count', {
        eventId,
        marketTypeGroupId: marketTypeGroupId === 'all' ? undefined : marketTypeGroupId,
        isLive,
      }),
    enabled: enabled && !!eventId && !!marketTypeGroupId,
  });
};

type UsePublishedMarketsOptions = {
  eventId: string | undefined;
  marketGroupId: string | undefined;
  isLive: boolean;
  limit?: number;
};

export const usePublishedMarkets = ({ eventId, marketGroupId, isLive, limit = 25 }: UsePublishedMarketsOptions) => {
  const { data: marketsCount } = useMarketsCount({
    eventId,
    marketTypeGroupId: marketGroupId,
    isLive,
    enabled: !!eventId && !!marketGroupId,
  });

  const {
    items: markets = [],
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
    isLoading,
    isFetching,
    isInitialLoading,
    isError,
    isSuccess,
    isPreviousData,
  } = useInfiniteScroll<Market>({
    queryKey: [QUERY_KEYS.markets, eventId, marketGroupId, isLive],
    endpoint: 'markets/published',
    queryParams: {
      eventId,
      marketTypeGroupId: marketGroupId === 'all' ? undefined : marketGroupId,
      limit,
      isLive,
    },
    enabled: !!eventId && !!marketGroupId,
    placeholderData: keepPreviousData,
    totalCount: marketsCount,
  });

  return {
    markets,
    marketsCount,
    isFetchingNextPage,
    hasNextPage,
    lastElementRef,
    isLoading,
    isFetching,
    isInitialLoading,
    isError,
    isSuccess,
    isPreviousData,
  };
};

type PublishedEventQuery = {
  queryKey?: QueryKey;
  queryParams?: {
    count?: boolean;
    [key: string]: unknown;
  };
  options?: Pick<
    UseQueryOptions<PublishedEventListWithTopMarketType>,
    'enabled' | 'refetchInterval' | 'placeholderData'
  >;
};

export const usePublishedEvents = ({
  queryKey = [QUERY_KEYS.publishedEvents],
  queryParams,
  options,
}: PublishedEventQuery = {}) => {
  return useQuery({
    queryKey,
    queryFn: (): Promise<PublishedEventListWithTopMarketType> =>
      getData(API_ROUTES.EVENTS.PUBLISHED, {
        page: 1,
        limit: 25,
        isActive: true,
        ...queryParams,
      }),
    ...options,
  });
};

type GroupedPublishedEventQuery = {
  queryKey?: QueryKey;
  endpoint?: string;
  queryParams?: {
    count?: boolean;
    [key: string]: unknown;
  };
  options?: Pick<UseQueryOptions<GroupedPublishedEventsResponse>, 'enabled' | 'refetchInterval' | 'placeholderData'>;
};

export const useGroupedPublishedEvents = ({
  queryKey = [QUERY_KEYS.publishedEvents, 'grouped'],
  endpoint = API_ROUTES.EVENTS.PUBLISHED_GROUPED,
  queryParams,
  options,
}: GroupedPublishedEventQuery = {}) => {
  return useQuery({
    queryKey,
    queryFn: (): Promise<GroupedPublishedEventsResponse> =>
      getData(endpoint, {
        page: 1,
        limit: 25,
        isActive: true,
        ...queryParams,
      }),
    ...options,
  });
};

export const useJackpotPools = () =>
  useQuery({
    queryKey: [QUERY_KEYS.jackpots],
    queryFn: (): Promise<PaginatedData<JackpotPool>> =>
      getData(API_ROUTES.JACKPOTS.POOLS, {
        page: 1,
        limit: 100,
        product: PRODUCT.SPORTSBOOK,
      }),
    select: (data) => data.items,
    refetchInterval: 30 * 1000,
  });

export const useCompetitions = ({
  sportId,
  type = 'match',
  isLive,
}: {
  sportId: string | undefined;
  type: EventType;
  isLive?: boolean;
}) =>
  useQuery({
    queryKey: [QUERY_KEYS.competitions, sportId, type, isLive],
    queryFn: (): Promise<PaginatedData<Competition>> =>
      getData('competitions/with-events', {
        limit: 200,
        sportId,
        type,
        isLive,
      }),
    enabled: !!sportId,
    staleTime: Infinity,
  });

export const useGlobalTicketConditions = () => {
  const { isPlayer } = useRoles();

  return useQuery({
    queryKey: [QUERY_KEYS.ticketConditions],
    queryFn: (): Promise<GlobalTicketConditionsResponse> => getData('conditions/global/ticket'),
    enabled: isPlayer,
  });
};

export const useUserData = () => {
  const { isPlayer } = useRoles();
  const { isAuthenticated, userId } = useAuth();
  const endpoint = isPlayer ? 'customer/profile' : 'agent/profile';

  const response = useQuery({
    queryKey: [QUERY_KEYS.customerData, endpoint, userId],
    queryFn: (): Promise<CustomerSettings> => getData(endpoint, undefined, 'crm'),
    staleTime: Infinity,
    enabled: isAuthenticated && Boolean(userId),
    refetchInterval: (query) => (query.state.data?.kycStatus === 'pending' ? 15 * SECOND : false),
  });

  const isCasinoOnlyClient = response.isSuccess && hasCasinoOnlyProducts(response.data?.products);
  const isSportsbookOnlyClient = response.isSuccess && hasSportsbookOnlyProducts(response.data?.products);

  return { ...response, isCasinoOnlyClient, isSportsbookOnlyClient };
};

export const useStartKycVerification = () => {
  const invalidateQuery = useInvalidateQuery();
  const { t } = useLocalization();
  const { data: userData } = useUserData();
  const { hasReachedLimit } = useKycAttempts(userData?.kycAttempts);

  return useMutation({
    mutationKey: [QUERY_KEYS.customerData, API_ROUTES.COMPLY_CUBE.FLOW_SESSION],
    mutationFn: (): Promise<ComplyCubeFlowSession> => {
      if (hasReachedLimit) {
        return Promise.reject(
          new Error(
            t(
              'kyc.maxAttemptsReachedMessage',
              "You've reached the maximum number of verification attempts. Our team will review your case and contact you if more information is needed."
            )
          )
        );
      }

      return postData(API_ROUTES.COMPLY_CUBE.FLOW_SESSION, {}, 'crm');
    },
    onSuccess: async (response) => {
      await invalidateQuery([QUERY_KEYS.customerData]);
      window.location.assign(response.redirectUrl);
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t('responseMessages.unexpectedErrorOccurred'), { variant: 'error' });
    },
  });
};

export const useUpdateUserData = () => {
  const invalidateQuery = useInvalidateQuery();
  const { isPlayer } = useRoles();
  const { userId } = useAuth();
  const endpoint = isPlayer ? 'customer/profile' : 'agent/profile';

  return useMutation({
    mutationKey: [QUERY_KEYS.customerData, endpoint, userId],
    mutationFn: (
      settings: Partial<Omit<CustomerSettings, 'emailVerified' | 'kycRequired' | 'kycStatus' | 'kycTrigger'>>
    ) => postData(endpoint, settings, 'crm'),
    onSuccess: () => {
      invalidateQuery([QUERY_KEYS.customerData]);
    },
  });
};

// Dedicated, set-true-only flag update. The internal firstDepositGTMEventSent flag is
// intentionally not part of the writable profile DTO, so it is persisted via this endpoint.
export const useMarkFirstDepositGtmEventSent = () => {
  const invalidateQuery = useInvalidateQuery();
  const { userId } = useAuth();

  return useMutation({
    mutationKey: [QUERY_KEYS.customerData, 'first-deposit-gtm-event', userId],
    mutationFn: () => postData('customer/profile/first-deposit-gtm-event', {}, 'crm'),
    onSuccess: () => {
      invalidateQuery([QUERY_KEYS.customerData]);
    },
  });
};

export const useUpdateAgentData = (userId: string) => {
  const { userId: agentId } = useAuth();
  const { isAgent, role } = useRoles();
  const endpointPrefix = isAgent ? 'customer' : 'agent';
  const { t } = useLocalization();
  return useMutation({
    mutationKey: [userId],
    mutationFn: (data: AgentDetailsUser) => {
      return patchData(`${endpointPrefix}/${userId}`, data, 'crm', {
        agentId,
        role,
      });
    },
    onSuccess: (_data, variables) => {
      if (variables.password) {
        enqueueSnackbar(t('register.validation.passwordUpdatedSuccessfully'), { variant: 'success' });
      } else {
        enqueueSnackbar(t('profileUpdatedSuccessfully'), { variant: 'success' });
      }
    },
    onError: () => {
      enqueueSnackbar(t('unexpectedError'), { variant: 'error' });
    },
  });
};

export const useBetslipById = (
  betslipId: string | undefined,
  options?: Omit<UseQueryOptions<BetslipWithBetsExtendedInfo>, 'queryKey' | 'queryFn'>
) => {
  const { isPlayer } = useRoles();

  return useQuery<BetslipWithBetsExtendedInfo>({
    queryKey: [QUERY_KEYS.betslip, betslipId],
    queryFn: () => {
      if (!betslipId) {
        throw new Error('Betslip id is required.');
      }

      const baseUrl = isPlayer ? 'betting-slips/my-bets' : 'betting-slips';
      return getData(`${baseUrl}/${betslipId}`);
    },
    ...options,
    enabled: !!betslipId && (options?.enabled ?? true),
    ...(!betslipId && { gcTime: 0 }),
  });
};

export const useUnreadMessageCount = () => {
  const { isPlayer } = useRoles();

  return useQuery({
    queryKey: [QUERY_KEYS.messageCount],
    queryFn: (): Promise<number> => getData('/messages-client/count'),
    refetchInterval: 60 * SECOND,
    enabled: isPlayer,
  });
};

export const useSlipBonusCampaigns = () =>
  useQuery({
    queryKey: [QUERY_KEYS.slipBonusCampaigns],
    queryFn: async (): Promise<SlipBonusCampaigns> => getData('slip-bonus-campaigns'),
  });

export const useOpenBetslipCount = () => {
  const { isPlayer } = useRoles();

  return useQuery({
    queryKey: [QUERY_KEYS.myBetsCount],
    queryFn: (): Promise<{ count: number }> => getData('/betting-slips/my-bets/count-open'),
    select: (data) => data.count,
    enabled: isPlayer,
  });
};

export const useGetWalletTransactions = (
  queryParams?: QueryParams,
  options?: Omit<UseQueryOptions<PaginatedData<WalletTransaction | CasinoTransaction>>, 'queryKey' | 'queryFn'>
) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer
    ? API_ROUTES.WALLET_TRANSACTIONS_HISTORY.PLAYER
    : API_ROUTES.WALLET_TRANSACTIONS_HISTORY.BACKOFFICE;
  return usePagination<WalletTransaction | CasinoTransaction>(endpoint, queryParams, 'wallet', options);
};

export const useInfiniteWalletTransactions = (queryParams?: Partial<QueryParams> & { limit?: number }) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer
    ? API_ROUTES.WALLET_TRANSACTIONS_HISTORY.PLAYER
    : API_ROUTES.WALLET_TRANSACTIONS_HISTORY.BACKOFFICE;

  return useInfiniteScroll<WalletTransaction | CasinoTransaction>({
    queryKey: [QUERY_KEYS.walletTransactions, endpoint],
    endpoint,
    queryParams,
    apiService: 'wallet',
  });
};

export const useGetWalletTransactionTotals = (queryParams?: { fromTimestamp?: number; toTimestamp?: number }) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer
    ? API_ROUTES.WALLET_TRANSACTION_TOTALS.PLAYER
    : API_ROUTES.WALLET_TRANSACTION_TOTALS.BACKOFFICE;

  return useQuery({
    queryKey: [QUERY_KEYS.walletTransactionTotals, endpoint, queryParams],
    queryFn: (): Promise<WalletTransactionTotals> => getData(endpoint, queryParams, 'wallet'),
  });
};

export const useGetBettingHistory = (
  queryParams?: QueryParams,
  options?: Omit<UseQueryOptions<PaginatedData<BetslipWithBetsExtendedInfo>>, 'queryKey' | 'queryFn'>
) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer ? API_ROUTES.BETTING_HISTORY.PLAYER : API_ROUTES.BETTING_HISTORY.BACKOFFICE;

  return usePagination<BetslipWithBetsExtendedInfo>(endpoint, queryParams, 'feed', options);
};

export const useInfiniteBettingHistory = (
  queryParams?: Partial<QueryParams>,
  options?: InfiniteScrollOptions<BetslipWithBetsExtendedInfo>
) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer ? API_ROUTES.BETTING_HISTORY.PLAYER : API_ROUTES.BETTING_HISTORY.BACKOFFICE;

  return useInfiniteScroll<BetslipWithBetsExtendedInfo>({
    queryKey: [QUERY_KEYS.betHistory, endpoint],
    endpoint,
    queryParams,
    apiService: 'feed',
    ...options,
  });
};

export const useGetReports = (
  queryParams?: QueryParams,
  options?: Omit<UseQueryOptions<PaginatedData<SportsbookReport>>, 'queryKey' | 'queryFn'>
) => {
  const { role } = useRoles();

  const ROLE_REPORT_ENDPOINTS: Partial<Record<UserRole, string>> = {
    [USER_ROLES.ADMIN]: 'reports/partners/managers',
    [USER_ROLES.MANAGER]: 'reports/partners/agents',
    [USER_ROLES.AGENT]: 'reports/players',
    [USER_ROLES.PLAYER]: 'reports-client/daily',
  };

  const endpoint = ROLE_REPORT_ENDPOINTS[role] ?? 'reports/players';

  return usePagination<SportsbookReport>(endpoint, queryParams, 'feed', options);
};

export const useInfiniteReports = (
  queryParams?: Partial<QueryParams> & { limit?: number },
  options?: InfiniteScrollOptions<SportsbookReport>
) => {
  const { role } = useRoles();

  const ROLE_REPORT_ENDPOINTS: Partial<Record<UserRole, string>> = {
    [USER_ROLES.ADMIN]: 'reports/partners/managers',
    [USER_ROLES.MANAGER]: 'reports/partners/agents',
    [USER_ROLES.AGENT]: 'reports/players',
    [USER_ROLES.PLAYER]: 'reports-client/daily',
  };

  const endpoint = ROLE_REPORT_ENDPOINTS[role] ?? 'reports/players';

  return useInfiniteScroll<SportsbookReport>({
    queryKey: [QUERY_KEYS.reports, endpoint],
    endpoint,
    queryParams,
    apiService: 'feed',
    ...options,
  });
};

export const useGetUserDetails = (userId: string) => {
  const { userId: loggedInUserId } = useAuth();
  const { role, isAgent } = useRoles();

  const endpointPrefix = isAgent ? 'customer/by-user' : 'agent/by-id';

  return useQuery({
    queryKey: [QUERY_KEYS.userDetails, userId],
    queryFn: (): Promise<Customer> =>
      getData(`${endpointPrefix}/${userId}?includeLastOnline=true`, undefined, 'crm', {
        agentId: loggedInUserId,
        role,
      }),
    enabled: !!userId,
  });
};

type AgentBalanceResponse = {
  bonus: UserBalance | null;
  main: UserBalance | null;
};

export const useGetAgentBalance = (queryParams?: { userId: string }) => {
  return useQuery({
    queryKey: [queryParams],
    queryFn: (): Promise<AgentBalanceResponse> => getData('wallets/user-balances', { ...queryParams }, 'wallet'),
    enabled: !!queryParams,
  });
};

export const useProfitData = (queryParams?: { fromTimestamp?: number; toTimestamp?: number }) => {
  const { isPlayer } = useRoles();
  const endpoint = isPlayer ? 'betting-slips/history-profit' : '/bo/betting-slips/history-profit';
  return useQuery({
    queryKey: [QUERY_KEYS.profitData, queryParams],
    queryFn: (): Promise<number> => getData(endpoint, queryParams, 'feed'),
    enabled: !!queryParams,
  });
};
